/**
 * @file QAInput 内 @ / # 触发编排：保持既有优先级与互斥，不改业务语义
 * @ 优先 → / 次之（带 @ 互斥）→ # 最后（带 / / @ 互斥）
 */
import { useMemoizedFn } from 'ahooks';

/**
 * useMentionTextareaOrchestration 选项
 * @interface IMentionTextareaOrchestrationOptions
 */
export interface IMentionTextareaOrchestrationOptions {
  /** 是否允许知识库 @ 提及 */
  allowKnowledgeMention: boolean;
  /** 是否允许技能 / 提及 */
  allowSlashMention: boolean;
  /** 是否允许图表 # 提及 */
  allowHashMention: boolean;
  /** / 模式是否已打开 */
  isSlashModeOpen: boolean;
  /** @ 模式是否已打开 */
  isKbAtModeOpen: boolean;
  /** 知识库 textarea onChange 包装函数 */
  kbWrapTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => boolean;
  /** 技能 textarea onChange 包装函数 */
  skillWrapTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => boolean;
  /** 图表 textarea onChange 包装函数 */
  chartWrapTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => boolean;
  /** 设置输入框值 */
  setInputValue: (value: string) => void;
  /** 知识库 textarea focus 回调 */
  handleKbTextareaFocus: () => void;
  /** 技能 textarea focus 回调 */
  handleSlashTextareaFocus: () => void;
  /** 图表 textarea focus 回调 */
  handleHashTextareaFocus: () => void;
}

/**
 * QAInput 内三种 Mention 触发编排 hook
 * 保持 @ → / → # 的优先级与互斥关系，确保不同 Mention 不会同时打开
 * @param options - hook 选项
 * @returns 编排后的 textarea change 和 focus 处理函数
 */
export function useMentionTextareaOrchestration({
  allowKnowledgeMention,
  allowSlashMention,
  allowHashMention,
  isSlashModeOpen,
  isKbAtModeOpen,
  kbWrapTextareaChange,
  skillWrapTextareaChange,
  chartWrapTextareaChange,
  setInputValue,
  handleKbTextareaFocus,
  handleSlashTextareaFocus,
  handleHashTextareaFocus,
}: IMentionTextareaOrchestrationOptions) {
  /**
   * 编排后的 textarea onChange 处理
   * @ 优先；开启 / 时未命中再试 #（带互斥）；未开 / 时 # 不带互斥
   */
  const handleMentionTextareaChange = useMemoizedFn(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (allowKnowledgeMention && kbWrapTextareaChange(e)) return;
      if (allowSlashMention) {
        if (skillWrapTextareaChange(e)) return;
        if (
          allowHashMention &&
          !isSlashModeOpen &&
          !isKbAtModeOpen &&
          chartWrapTextareaChange(e)
        ) {
          return;
        }
        setInputValue(e.target.value);
        return;
      }
      if (allowHashMention && chartWrapTextareaChange(e)) return;
      setInputValue(e.target.value);
    },
  );

  /**
   * 编排后的 textarea focus/click 处理
   * 按优先级依次触发各 mention 的 focus 重检
   */
  const handleMentionFocusOrClick = useMemoizedFn(() => {
    if (allowKnowledgeMention) handleKbTextareaFocus();
    if (allowSlashMention && !isKbAtModeOpen) handleSlashTextareaFocus();
    if (allowHashMention && !isSlashModeOpen && !isKbAtModeOpen) {
      handleHashTextareaFocus();
    }
  });

  return {
    handleMentionTextareaChange,
    handleMentionFocusOrClick,
  };
}
